import Route from '@ember/routing/route';
import type PublicTransition from '@ember/routing/transition';
import { service } from '@ember/service';

import type { APIData, Service } from 'webapp-types';
import { ActivityLogEvent } from 'webapp/enums/activity-log';
import { ModelName } from 'webapp/enums/enums';

import type BoardRecord from 'webapp/models/board';

export default class MobileBoardRoute extends Route {
	@service('store') declare storeService: Service.Store;
	@service('request') declare requestService: Service.Request;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/activity-logs')
	declare mmActivityLogsService: Service.ModelManager.ActivityLogs;

	override beforeModel(transition: PublicTransition): Promise<void> {
		const boardToken = transition?.to?.params?.board_token;

		let boardPromise;
		if (boardToken) {
			boardPromise = this.requestService
				.api<APIData.Boards.ListResponse>('GET', '/boards')
				.then((payload) => {
					const thisBoardData = (payload.boards || []).find(
						(item) => item.token === boardToken,
					);
					if (thisBoardData) {
						this.storeService.pushPayload({ board: thisBoardData });
						this.mmActivityLogsService.createAndSaveActivityLogItem(
							ActivityLogEvent.BOARD_VIEW,
							String(thisBoardData.id),
						);
					}
				})
				.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
		} else {
			boardPromise = Promise.reject();
		}

		const promises = [this.navigationService.initialRequestsPromise, boardPromise];

		return new Promise((resolve /*, reject*/) => {
			void Promise.allSettled(promises).then((statusArray) => {
				const rejectedRequests = statusArray.filter((item) => item.status === 'rejected');

				if (rejectedRequests.length) {
					this.infolayerService.error('There was an error requesting your data');
				} else {
					resolve();
				}
			});
		});
	}

	override model(params: { board_token: string }): BoardRecord {
		const model = this.storeService
			.peekAll(ModelName.BOARD)
			.find((item) => (item as BoardRecord).token === params.board_token);

		return model as BoardRecord;
	}

	override afterModel(model: BoardRecord): void {
		if (model.id) {
			void this.mmBoardsService.requestBoardData(model.id);
		}
	}
}
